import { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  LoyaltyProgramRewardFragment,
  LoyaltyRewardAvailability,
  LoyaltyRewardWhereInput,
  SortOrder,
  useGetLoyaltyProgramRewardsLazyQuery,
} from 'shared/types/graphql';

export const useGetRewards = (
  programId?: string,
  customWhere?: LoyaltyRewardWhereInput,
): {
  rewards: LoyaltyProgramRewardFragment[];
  rewardsCount: number;
  fetchMore: () => Promise<void>;
  loading: boolean;
} => {
  const [take, setTake] = useState(5);

  const [getRewards, { data: rewardsData, fetchMore: fetchMoreRewards, loading: areRewardsLoading }] =
    useGetLoyaltyProgramRewardsLazyQuery({
      variables: {
        where: {
          programId: {
            equals: programId,
          },
          availability: {
            equals: LoyaltyRewardAvailability.Published,
          },
          ...customWhere,
        },
        orderBy: [{ pointCost: SortOrder.Asc }],
        take,
      },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  useEffect(() => {
    if (!!programId) {
      getRewards();
    }
  }, [getRewards, programId]);

  const rewards = useMemo((): LoyaltyProgramRewardFragment[] => {
    return rewardsData?.findManyLoyaltyReward || [];
  }, [rewardsData]);

  const rewardsCount = rewardsData?.findManyLoyaltyRewardCount || 0;

  const loadMoreRewardsEntries = useCallback(() => {
    if (programId && fetchMoreRewards) {
      setTake((curr) => {
        const count = curr + rewards.length;
        fetchMoreRewards({
          variables: {
            take: count,
          },
        });
        return count;
      });
    }
  }, [fetchMoreRewards, programId, rewards.length]);

  return {
    rewards,
    rewardsCount,
    fetchMore: async (): Promise<void> => {
      await loadMoreRewardsEntries();
    },
    loading: areRewardsLoading,
  };
};
